import { useCallback } from 'react';

import { useQuery } from '@apollo/client';

import {
  searchTasksQuery,
  taskFilterOptionsQuery,
  tasksQuery,
} from 'graphql-queries';
import { TaskFilterOptionType } from 'graphql-query-contracts';

type TaskSearchVariables = {
  groupIds: string[];
  search?: object;
  pagination?: { start: number; perPage: number };
  filter: object;
  preFilter?: object;
};

type Props = {
  channelId?: string;
  variables: TaskSearchVariables;
  isSplitSearch: boolean;
};

const ALL_FILTER_OPTION_TYPES = Object.values(TaskFilterOptionType);

export function useTaskListData({
  channelId,
  variables,
  isSplitSearch,
}: Props) {
  // skip, not a condition, turns a query off (hook order); searchTasks goes away with the flag
  const searchResult = useQuery(searchTasksQuery, {
    skip: !channelId || isSplitSearch,
    variables,
    fetchPolicy: 'network-only',
  });
  const tasksResult = useQuery(tasksQuery, {
    skip: !channelId || !isSplitSearch,
    variables,
    fetchPolicy: 'network-only',
  });
  const filterOptionsResult = useQuery(taskFilterOptionsQuery, {
    skip: !channelId || !isSplitSearch,
    variables: {
      groupIds: variables.groupIds,
      options: ALL_FILTER_OPTION_TYPES,
    },
    fetchPolicy: 'network-only',
  });

  const refetchSearch = searchResult.refetch;
  const refetchTasks = tasksResult.refetch;
  const refetchFilterOptions = filterOptionsResult.refetch;
  const refreshAfterTaskChange = useCallback(async () => {
    if (!isSplitSearch) {
      await refetchSearch();

      return;
    }

    const tasksRefetch = refetchTasks();
    const filterOptionsRefetch = refetchFilterOptions();
    // a failed refresh keeps the lists already shown and must not fail the task change
    const filterOptionsRefresh = filterOptionsRefetch.catch(() => undefined);

    await Promise.all([tasksRefetch, filterOptionsRefresh]);
  }, [isSplitSearch, refetchSearch, refetchTasks, refetchFilterOptions]);

  if (!isSplitSearch) {
    return {
      tasks: searchResult.data?.searchTasks?.tasks,
      pageInfo: searchResult.data?.searchTasks?.pageInfo,
      filterOptions: (searchResult.data ?? searchResult.previousData)
        ?.searchTasks?.filterOptions,
      loading: searchResult.loading,
      error: searchResult.error,
      refreshAfterTaskChange,
    };
  }

  return {
    tasks: tasksResult.data?.tasks?.tasks,
    pageInfo: tasksResult.data?.tasks?.pageInfo,
    filterOptions: (
      filterOptionsResult.data ?? filterOptionsResult.previousData
    )?.taskFilterOptions,
    loading: tasksResult.loading,
    error: tasksResult.error,
    refreshAfterTaskChange,
  };
}
